import type { AtendenteDTO, ConversaDetalheDTO, MensagemDTO } from '@viniko/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Pin, PinOff } from 'lucide-react';
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useToast } from '../../componentes/ui';
import { patch, post } from '../../lib/api';
import { chaves, useAtendentes } from '../../lib/consultas';
import { useCorAtendente } from '../../lib/tema';
import { quandoCurto } from '../../lib/tempo';

function comMencoes(texto: string) {
  return texto.split(/(@[\p{L}\d_]+)/u).map((parte, i) =>
    parte.startsWith('@') ? (
      <b key={i} className="mencao">
        {parte}
      </b>
    ) : (
      parte
    ),
  );
}

export function Observacoes({
  conversa: c,
  eu,
}: {
  conversa: ConversaDetalheDTO;
  eu: AtendenteDTO;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const atendentes = useAtendentes().data;
  const [texto, setTexto] = useState('');
  const [fixar, setFixar] = useState(false);
  const [noContato, setNoContato] = useState(false);
  const [mencionados, setMencionados] = useState<Map<string, string>>(new Map());
  const [salvando, setSalvando] = useState(false);
  const ta = useRef<HTMLTextAreaElement>(null);

  const lista = c.mensagens
    .filter((m) => m.interna)
    .sort((a, b) => Number(b.fixada) - Number(a.fixada) || b.enviadoEm.localeCompare(a.enviadoEm));

  const termo = /(^|\s)@([\p{L}\d_]*)$/u.exec(texto)?.[2];
  const sugestoes = useMemo(
    () =>
      termo === undefined
        ? []
        : (atendentes ?? [])
            .filter(
              (a) =>
                a.ativo &&
                a.id !== eu.id &&
                a.nomeExibicao.toLowerCase().startsWith(termo.toLowerCase()),
            )
            .slice(0, 5),
    [termo, atendentes, eu.id],
  );

  function mencionar(a: AtendenteDTO) {
    setTexto((t) => t.replace(/@([\p{L}\d_]*)$/u, `@${a.nomeExibicao} `));
    setMencionados((m) => new Map(m).set(a.id, a.nomeExibicao));
    ta.current?.focus();
  }

  async function salvar() {
    const t = texto.trim();
    if (!t || salvando) return;
    setSalvando(true);
    try {
      const m = await post<MensagemDTO>(`/api/conversas/${c.id}/observacoes`, {
        texto: t,
        fixada: fixar,
        salvarNoContato: noContato,
        mencionados: [...mencionados]
          .filter(([, nome]) => t.includes(`@${nome}`))
          .map(([id]) => id),
      });
      qc.setQueryData<ConversaDetalheDTO>(chaves.conversa(c.id), (d) =>
        d && !d.mensagens.some((x) => x.id === m.id) ? { ...d, mensagens: [...d.mensagens, m] } : d,
      );
      if (noContato) qc.invalidateQueries({ queryKey: chaves.conversa(c.id) });
      setTexto('');
      setFixar(false);
      setNoContato(false);
      setMencionados(new Map());
    } catch (e) {
      toast((e as Error).message, 'erro');
    } finally {
      setSalvando(false);
    }
  }

  function teclas(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (sugestoes.length && (e.key === 'Enter' || e.key === 'Tab')) {
      e.preventDefault();
      mencionar(sugestoes[0]!);
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void salvar();
    }
  }

  return (
    <div className="obs">
      <div className="obs-campo">
        <textarea
          id="obs-texto"
          ref={ta}
          rows={2}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={teclas}
          placeholder="Escreva uma observação para a equipe… (@ menciona um colega)"
        />
        {sugestoes.length > 0 && (
          <div className="sugestoes entra" role="listbox" aria-label="Mencionar atendente">
            {sugestoes.map((a) => (
              <button key={a.id} role="option" aria-selected={false} onClick={() => mencionar(a)}>
                @{a.nomeExibicao} <small>{a.nome}</small>
              </button>
            ))}
          </div>
        )}
        {texto.trim() && (
          <div className="obs-acoes">
            <label className="check">
              <input
                id="obs-fixar"
                type="checkbox"
                checked={fixar}
                onChange={(e) => setFixar(e.target.checked)}
              />{' '}
              Fixar
            </label>
            <label className="check" title="Aparece em todas as próximas conversas deste cliente">
              <input
                id="obs-contato"
                type="checkbox"
                checked={noContato}
                onChange={(e) => setNoContato(e.target.checked)}
              />{' '}
              Salvar no contato
            </label>
            <button className="btn primario" disabled={salvando} onClick={() => void salvar()}>
              Salvar
            </button>
          </div>
        )}
      </div>

      {lista.length > 0 && (
        <ul className="obs-itens">
          {lista.map((m) => (
            <ItemObservacao key={m.id} m={m} />
          ))}
        </ul>
      )}

      {c.observacoesContato.length > 0 && (
        <>
          <p className="obs-subtitulo">Salvas no contato</p>
          <ul className="obs-itens do-contato">
            {c.observacoesContato.map((o) => (
              <li key={o.id}>
                <p>{comMencoes(o.texto)}</p>
                <small>
                  {o.autor} · {quandoCurto(o.criadoEm)}
                </small>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ItemObservacao({ m }: { m: MensagemDTO }) {
  const toast = useToast();
  const cor = useCorAtendente(m.atendente?.cor);
  return (
    <li className={m.fixada ? 'obs-fixada' : ''}>
      <div className="obs-topo">
        <span className="dono-txt">
          <span className="dono-ponto" style={{ background: cor.texto }} />
          <b>{m.atendente?.nomeExibicao ?? 'Equipe'}</b>
        </span>
        <small>{quandoCurto(m.enviadoEm)}</small>
        <button
          className="icone-btn sem-borda"
          aria-label={m.fixada ? 'Desafixar' : 'Fixar'}
          title={m.fixada ? 'Desafixar' : 'Fixar no topo'}
          onClick={() =>
            patch(`/api/mensagens/${m.id}/fixar`, { fixada: !m.fixada }).catch((e) =>
              toast(e.message, 'erro'),
            )
          }
        >
          {m.fixada ? <PinOff /> : <Pin />}
        </button>
      </div>
      <p>{comMencoes(m.conteudo)}</p>
    </li>
  );
}
